import {Route, Routes, useLocation, useNavigate} from 'react-router-dom'
import Login from './auth/Login'
import React, {useEffect, useState} from 'react'
import {webEvents} from './common/WebEvents'
import {useAuth} from './auth/auth'
import Conduct from './inventory/counts/Conduct'
import {APP_VERSION, WEBEVENTS_URI} from './common/environment'
import {Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography} from '@mui/material'
import GavelIcon from '@mui/icons-material/Gavel'
import BySample from './inventory/counts/sample/BySample'
import ByBin from './inventory/counts/bin/ByBin'
import ReviewLandingPage from './inventory/counts/review/ReviewLandingPage'
import ReviewPage from './inventory/counts/review/ReviewPage'
import ErrorDialog from './common/ErrorDialog'
import NavBar from './common/NavBar'
import Manage from './inventory/counts/manage/Manage'
import {RouteSecurity} from './common/RouteSecurity'
import CountsDashboard from './inventory/counts/CountsDashboard'
import AppDashboard from './AppDashboard'
import Receive from './inventory/receive/Receive'
import ReceiveDashboard from './inventory/receive/ReceiveDashboard'
import ManageClockIn from './auth/ManageClockIn'
import usePollingEffect from './common/usePollingEffect'
import RequestTransfer from './inventory/transfers/RequestTransfer'
import CreateTransferRequest from './inventory/transfers/CreateTransferRequest'
import TransferRequestsReviewDashboard from './inventory/transfers/TransferRequestReviewDashboard'
import TransferRequestReview from './inventory/transfers/TransferRequestReview'
import TransferSerialSelection from './inventory/transfers/TransferSerialSelection'
import TransfersDashboard from './inventory/transfers/TransfersDashboard'

const JWT_EXPIRED_MSG = 'Your session has expired. Please log back in to continue'

function App() {
    const {isLoggedIn, token, isTokenExpired, getPermissions, logout, getUser} = useAuth()
    const [openVersionDialog, setOpenVersionDialog] = useState(false)
    const [shouldRedirectToLogin, setShouldRedirectToLogin] = useState(false)
    const location = useLocation()
    const navigate = useNavigate()

    function RequireAuth({children}: {children: any}) {
        useEffect(() => {
            const user = getUser()
            if (!user.token && location.pathname !== '/login') {
                setShouldRedirectToLogin(true)
            }
        }, [])

        if (isLoggedIn) {
            let routeSecurity = new RouteSecurity(getPermissions())

            /* Prevent user from going back to log in screen once they've logged in and route to
            *  correct landing page based on their role */
            if (location.pathname === '/login' || location.pathname === '/') {
            } else if (!routeSecurity.isAuthorized(location.pathname)) {
                /* Prevent User from accessing pages they don't have access to */
                return <DisplayUnAuthorized />
            }

            return <>{children}</>
        }

        return <></>
    }

    function ReloadAppVersionDialog() {
        return (
            <Dialog open={true}>
                <DialogTitle id="update-dialog-title">
                    New Version Available
                </DialogTitle>
                <DialogContent>
                    <div id="update-dialog-description">
                        There&apos;s a new version of TechGo. Please update the
                        app.
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            window.location.reload()
                        }}
                        color="primary"
                        variant="contained">
                        Update
                    </Button>
                </DialogActions>
            </Dialog>
        )
    }

    function DisplayUnAuthorized() {
        return (
            <Box
                display="flex"
                justifyContent="center"
                flexDirection="column"
                alignItems="center"
                height="100vh"
                textAlign="center"
            >
                <GavelIcon style={{fontSize: '5rem', marginBottom: '16px'}} />
                <Typography
                    variant="h2"
                    component="h2"
                    fontWeight="bold"
                    style={{fontSize: '2rem'}}
                >
                    You do not have access to this page
                </Typography>
            </Box>
        )
    }

    function Display404Error() {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="100vh"
                textAlign="center"
            >
                <Typography
                    variant="h2"
                    component="h2"
                    fontWeight="bold"
                    style={{fontSize: '2rem'}}
                >
                    Oops! This page does not exist. Please enter the correct url.
                </Typography>
            </Box>
        )
    }

    usePollingEffect(async () => {
        const {pathname} = location
        if (isTokenExpired() && !pathname.includes('/login') && isLoggedIn) {
            logout(JWT_EXPIRED_MSG, true)
        }
    }, {interval: 1000}, [isTokenExpired, isLoggedIn])

    useEffect(() => {
        if (isLoggedIn) {
            webEvents.connect(WEBEVENTS_URI, {
                version: APP_VERSION,
                h3token: token
            })
        }

        webEvents.mergeOptions({
            onUpdateVersion: () =>
                setOpenVersionDialog(true),
            onError: (err: Event) => console.error('WebSocket error:', err)
        })
    }, [token, isLoggedIn])

    useEffect(() => {
        if (location.pathname.slice() === '/' && location.pathname !== '/') {
            navigate(location.pathname.slice(0, -1))
        }
    }, [location.pathname, navigate])

    useEffect(() => {
        if (shouldRedirectToLogin) {
            setShouldRedirectToLogin(false)
            navigate('/login')
        }
    }, [shouldRedirectToLogin, navigate])

    return (

        <div className="flex flex-col h-screen">
            {isLoggedIn && <NavBar />}
            <main className="flex-1 flex flex-col contenÏt-center overflow-y-scroll">
                {openVersionDialog && <ReloadAppVersionDialog />}
                <ErrorDialog />
                <Routes>
                    <Route
                        path="/login"
                        element={<Login />}
                    />
                    <Route
                        path="/"
                        element={
                            <RequireAuth>
                                <AppDashboard />
                            </RequireAuth>
                        }
                    />
                    <Route
                        path="/inventory/counts"
                        element={
                            <RequireAuth>
                                <CountsDashboard />
                            </RequireAuth>
                        }
                    />
                    <Route
                        path="/inventory/counts/conduct/*"
                        element={
                            <RequireAuth>
                                <Conduct />
                            </RequireAuth>
                        }
                    />
                    <Route
                        path="/inventory/counts/bin/*"
                        element={
                            <RequireAuth>
                                <ByBin />
                            </RequireAuth>
                        }
                    />
                    <Route
                        path="/inventory/counts/sample/*"
                        element={
                            <RequireAuth>
                                <BySample />
                            </RequireAuth>
                        }
                    />
                    <Route
                        path="/inventory/counts/review/*"
                        element={
                            <RequireAuth>
                                <ReviewLandingPage />
                            </RequireAuth>
                        }
                    />
                    <Route
                        path="/inventory/counts/review/:id"
                        element={
                            <RequireAuth>
                                <ReviewPage />
                            </RequireAuth>
                        }
                    />
                    <Route
                        path="/inventory/counts/manage/*"
                        element={
                            <RequireAuth>
                                <Manage />
                            </RequireAuth>
                        }
                    />
                    <Route
                        path="/inventory/receive/"
                        element={
                            <RequireAuth>
                                <ReceiveDashboard />
                            </RequireAuth>
                        }
                    />
                    <Route
                        path="/inventory/receiving"
                        element={
                            <RequireAuth>
                                <Receive />
                            </RequireAuth>
                        }
                    />
                    <Route
                        path="/clock-in"
                        element={
                            <RequireAuth>
                                <ManageClockIn />
                            </RequireAuth>
                        }
                    />
                    <Route
                        path="/inventory/transfers"
                        element={
                            <RequireAuth>
                                <TransfersDashboard />
                            </RequireAuth>
                        }
                    />
                    <Route
                        path="/inventory/transfer-requests"
                        element={
                            <RequireAuth>
                                <RequestTransfer />
                            </RequireAuth>
                        }
                    />
                    <Route
                        path="/inventory/transfer-requests/create"
                        element={
                            <RequireAuth>
                                <CreateTransferRequest />
                            </RequireAuth>
                        }
                    />
                    <Route
                        path="/transfer-requests/manage"
                        element={
                            <RequireAuth>
                                <TransferRequestsReviewDashboard />
                            </RequireAuth>
                        }
                    />
                    <Route
                        path="/transfer-requests/manage/:id"
                        element={
                            <RequireAuth>
                                <TransferRequestReview />
                            </RequireAuth>
                        }
                    />
                    <Route
                        path="/transfer-requests/:id/select-serials"
                        element={
                            <RequireAuth>
                                <TransferSerialSelection />
                            </RequireAuth>
                        }
                    />
                    <Route
                        path="*"
                        element={
                            <Display404Error />
                        }
                    />
                </Routes>
            </main>

            {isLoggedIn && (
                <footer className="bg-black text-white px-4 lg:px-6 py-6 text-sm">
                    v{APP_VERSION} &#x1F41D; High Tech Locksmiths &copy;{' '}
                    {new Date().getFullYear()}. All rights reserved.
                </footer>
            )}
        </div>
    )
}

export default App
